import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';
import { profileAPI, UserProfile } from '@/lib/supabase/profile';
import { useTheme } from '@/hooks/useTheme';
import { useCurrentTenantId } from '@/contexts/TenantContext';
import { uploadProfilePhoto } from '@/lib/supabase/storage';
import { supabase } from '@/lib/supabase/client';
import { User, Mail, Phone, Edit, Save, X, LogOut, Upload, Shield, Settings } from 'lucide-react';
import { toast } from 'sonner';

interface ProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FormData {
  first_name: string;
  last_name: string;
  phone_number: string;
  job_title: string;
  department: string;
}

/* eslint-disable max-lines-per-function, complexity, max-len, react/no-array-index-key */
export const ProfileDialog: React.FC<ProfileDialogProps> = ({ open, onOpenChange }) => {
  const { user, signOut, userProfile } = useAuth();
  const { theme, setTheme } = useTheme();
  const tenantId = useCurrentTenantId();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);

  const [formData, setFormData] = useState<FormData>({
    first_name: '',
    last_name: '',
    phone_number: '',
    job_title: '',
    department: ''
  });

  useEffect(() => {
    if (open && user?.id) {
      loadProfileData();
    } else {
      // Reset edit mode when dialog closes
      setIsEditing(false);
    }
  }, [open, user?.id]);

  useEffect(() => {
    if (profile) {
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        phone_number: profile.phone_number || '',
        job_title: profile.job_title || '',
        department: profile.department || ''
      });
    }
  }, [profile]);

  const loadProfileData = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const profileData = await profileAPI.getUserProfile(user.id);
      console.log('Loaded profile data:', profileData);
      console.log('Profile photo URL in loaded data:', profileData?.profile_photo_url);
      setProfile(profileData);
    } catch (error) {
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user?.id || !tenantId) {
      if (!tenantId) {
        toast.error('Tenant ID not available. Please refresh the page.');
      }
      return;
    }

    try {
      setUploadingPhoto(true);
      const result = await uploadProfilePhoto(tenantId, user.id, file);

      if (result.error) {
        console.error('Photo upload error:', result.error);
        toast.error(result.error);
        return;
      }

      if (!result.url) {
        console.error('Photo upload failed: No URL returned');
        toast.error('Failed to upload photo. Please check your browser console for details.');
        return;
      }

      // Update profile with new photo URL (use path from result, which will be stored in DB)
      console.log('Updating profile with photo URL:', result.url);
      console.log('Photo storage path:', result.path);
      const updatedProfile = await profileAPI.updateUserProfile(user.id, {
        profile_photo_url: result.url // Pass URL, API will extract path internally
      });

      if (updatedProfile) {
        console.log('Profile updated successfully:', updatedProfile);
        console.log('Profile photo URL in updated profile:', updatedProfile.profile_photo_url);
        setProfile(updatedProfile);
        // Reload profile data to ensure UI is in sync with latest auth metadata
        // Small delay to ensure auth session is updated
        setTimeout(async () => {
          await loadProfileData();
        }, 500);
        toast.success('Profile photo updated successfully');
      } else {
        console.error('Failed to update profile - updatedProfile is null');
        toast.error('Failed to update profile photo URL in database');
      }
    } catch (error) {
      console.error('Photo upload exception:', error);
      toast.error(
        error instanceof Error
          ? `Failed to upload photo: ${error.message}`
          : 'Failed to upload profile photo'
      );
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSaveProfile = async () => {
    if (!user?.id) return;

    try {
      setSaving(true);

      // Update auth metadata with full_name if first_name or last_name changed
      if (formData.first_name || formData.last_name) {
        const fullName = `${formData.first_name} ${formData.last_name}`.trim();
        const { error: authError } = await supabase.auth.updateUser({
          data: {
            full_name: fullName || null
          }
        });

        if (authError) {
          console.error('Error updating auth metadata:', authError);
          toast.error('Failed to update name. Please try again.');
          return;
        }
      }

      const updateData = {
        first_name: formData.first_name || null,
        last_name: formData.last_name || null,
        phone_number: formData.phone_number || null,
        job_title: formData.job_title || null,
        department: formData.department || null
      };

      let result;
      if (profile) {
        result = await profileAPI.updateUserProfile(user.id, updateData);
      } else {
        result = await profileAPI.createUserProfile({ user_id: user.id, ...updateData });
      }

      if (result) {
        setProfile(result);
        setIsEditing(false);
        toast.success('Profile updated successfully');
        // Reload profile data to ensure UI is in sync
        await loadProfileData();
      } else {
        toast.error('Failed to update profile');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    // Reset form data to profile values
    if (profile) {
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        phone_number: profile.phone_number || '',
        job_title: profile.job_title || '',
        department: profile.department || ''
      });
    }
    setIsEditing(false);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      onOpenChange(false);
      toast.success('Signed out successfully');
    } catch (error) {
      toast.error('Failed to sign out');
    }
  };

  // Create a default profile if none exists
  const displayProfile = profile || {
    id: '',
    user_id: user?.id || '',
    first_name: '',
    last_name: '',
    profile_photo_url: '',
    phone_number: '',
    job_title: '',
    department: '',
    preferred_language: 'en',
    timezone: 'UTC',
    profile_completion_percentage: 0,
    onboarding_step: 'profile_setup',
    onboarding_completed: false,
    onboarding_completed_at: '',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };

  const displayName =
    displayProfile?.first_name && displayProfile?.last_name
      ? `${displayProfile.first_name} ${displayProfile.last_name}`
      : user?.email?.split('@')[0] || 'User';

  // Debug: Log profile photo URL when it changes
  useEffect(() => {
    // Profile photo URL is optional, no need to log
  }, [displayProfile?.profile_photo_url]);

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className='max-w-2xl'>
          <DialogHeader>
            <DialogTitle>Profile</DialogTitle>
            <DialogDescription>Loading your profile information...</DialogDescription>
          </DialogHeader>
          <div className='animate-pulse space-y-4 py-8'>
            <div className='flex items-center gap-4'>
              <div className='h-20 w-20 rounded-full bg-muted' />
              <div className='space-y-2 flex-1'>
                <div className='h-4 bg-muted rounded w-1/3' />
                <div className='h-3 bg-muted rounded w-1/2' />
              </div>
            </div>
            <div className='space-y-3'>
              <div className='h-4 bg-muted rounded' />
              <div className='h-4 bg-muted rounded w-5/6' />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-2xl h-[90vh] flex flex-col p-0 gap-0 overflow-hidden'>
        <DialogHeader className='p-6 pb-4 shrink-0'>
          <DialogTitle className='flex items-center gap-2'>
            <User className='h-5 w-5' />
            Profile
          </DialogTitle>
          <DialogDescription>Manage your account information and preferences</DialogDescription>
        </DialogHeader>

        <div className='flex-1 overflow-hidden flex flex-col min-h-0'>
          <Tabs defaultValue='profile' className='flex flex-col h-full w-full min-h-0'>
            <div className='px-6 shrink-0'>
              <TabsList className='grid w-full grid-cols-2 mb-4'>
                <TabsTrigger value='profile'>Profile</TabsTrigger>
                <TabsTrigger value='appearance'>Appearance</TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value='profile' className='mt-0 flex-1 overflow-y-auto p-6 pt-4'>
              <div className='space-y-6'>
                {/* Profile Photo Section */}
                <div className='flex flex-col items-center gap-4 pb-6'>
                  <div className='relative'>
                    <Avatar className='h-24 w-24 ring-2 ring-primary/20 ring-offset-2'>
                      <AvatarImage
                        src={displayProfile?.profile_photo_url || undefined}
                        alt={`${displayName}'s profile photo`}
                        onError={e => {
                          console.error(
                            'Avatar image failed to load:',
                            displayProfile?.profile_photo_url
                          );
                          console.error('Error event:', e);
                        }}
                        onLoad={() => {
                          console.log(
                            'Avatar image loaded successfully:',
                            displayProfile?.profile_photo_url
                          );
                        }}
                      />
                      <AvatarFallback className='text-2xl bg-primary text-primary-foreground'>
                        {displayName.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    {userProfile?.roles && userProfile.roles.length > 0 && (
                      <div className='absolute -bottom-1 -right-1 bg-primary text-primary-foreground rounded-full p-1.5'>
                        <Shield className='h-4 w-4' />
                      </div>
                    )}
                    {uploadingPhoto && (
                      <div className='absolute inset-0 bg-black/50 rounded-full flex items-center justify-center'>
                        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-white' />
                      </div>
                    )}
                  </div>
                  <div className='text-center space-y-1'>
                    <h3 className='text-xl font-semibold'>{displayName}</h3>
                    <p className='text-sm text-muted-foreground'>
                      {displayProfile.job_title || 'No job title'}
                    </p>
                    {displayProfile.department && (
                      <p className='text-xs text-muted-foreground'>{displayProfile.department}</p>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type='file'
                    accept='image/jpeg,image/jpg,image/png,image/webp'
                    onChange={handlePhotoUpload}
                    className='hidden'
                    id='profile-photo-upload'
                    disabled={uploadingPhoto}
                  />
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    disabled={uploadingPhoto}
                    className='w-auto'
                    onClick={() => {
                      if (fileInputRef.current && !uploadingPhoto) {
                        fileInputRef.current.click();
                      }
                    }}
                  >
                    {uploadingPhoto ? (
                      <>
                        <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2' />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className='h-4 w-4 mr-2' />
                        {displayProfile?.profile_photo_url ? 'Change Photo' : 'Upload Photo'}
                      </>
                    )}
                  </Button>
                  <p className='text-xs text-muted-foreground text-center max-w-xs'>
                    JPEG, PNG, or WebP. Max 5MB. Image will be automatically compressed.
                  </p>
                </div>

                <Separator />

                {/* Profile Information Section */}
                <div className='space-y-4'>
                  <div className='flex items-center justify-between'>
                    <div className='flex items-center gap-2'>
                      <h4 className='font-semibold text-sm uppercase tracking-wide text-muted-foreground'>
                        Contact Information
                      </h4>
                      {isEditing && (
                        <span className='text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary'>
                          Editing
                        </span>
                      )}
                    </div>
                    {!isEditing && (
                      <Button
                        variant='ghost'
                        size='sm'
                        onClick={() => setIsEditing(true)}
                        className='h-8'
                      >
                        <Edit className='h-4 w-4 mr-2' />
                        Edit
                      </Button>
                    )}
                  </div>

                  <div className='grid gap-4 sm:grid-cols-2'>
                    {/* First Name */}
                    <div className='space-y-2'>
                      <Label
                        htmlFor='first_name'
                        className='text-sm font-medium flex items-center gap-2'
                      >
                        <User className='h-4 w-4 text-muted-foreground' />
                        First Name
                      </Label>
                      {isEditing ? (
                        <Input
                          id='first_name'
                          value={formData.first_name}
                          onChange={e => handleInputChange('first_name', e.target.value)}
                          placeholder='John'
                        />
                      ) : (
                        <div className='px-3 py-2 border rounded-md bg-background min-h-[40px] flex items-center'>
                          {displayProfile.first_name || (
                            <span className='text-muted-foreground text-sm'>Not set</span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Last Name */}
                    <div className='space-y-2'>
                      <Label
                        htmlFor='last_name'
                        className='text-sm font-medium flex items-center gap-2'
                      >
                        <User className='h-4 w-4 text-muted-foreground' />
                        Last Name
                      </Label>
                      {isEditing ? (
                        <Input
                          id='last_name'
                          value={formData.last_name}
                          onChange={e => handleInputChange('last_name', e.target.value)}
                          placeholder='Doe'
                        />
                      ) : (
                        <div className='px-3 py-2 border rounded-md bg-background min-h-[40px] flex items-center'>
                          {displayProfile.last_name || (
                            <span className='text-muted-foreground text-sm'>Not set</span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Email - Read Only */}
                    <div className='space-y-2 sm:col-span-2'>
                      <Label
                        htmlFor='email'
                        className='text-sm font-medium flex items-center gap-2'
                      >
                        <Mail className='h-4 w-4 text-muted-foreground' />
                        Email
                      </Label>
                      <Input
                        id='email'
                        value={user?.email || ''}
                        disabled
                        className='bg-muted/50'
                        readOnly
                      />
                      <p className='text-xs text-muted-foreground'>
                        Email cannot be changed. Contact your administrator if you need to update
                        it.
                      </p>
                    </div>

                    {/* Phone Number */}
                    <div className='space-y-2'>
                      <Label
                        htmlFor='phone_number'
                        className='text-sm font-medium flex items-center gap-2'
                      >
                        <Phone className='h-4 w-4 text-muted-foreground' />
                        Phone Number
                      </Label>
                      {isEditing ? (
                        <Input
                          id='phone_number'
                          value={formData.phone_number}
                          onChange={e => handleInputChange('phone_number', e.target.value)}
                          placeholder='+1 (555) 123-4567'
                        />
                      ) : (
                        <div className='px-3 py-2 border rounded-md bg-background min-h-[40px] flex items-center'>
                          {displayProfile.phone_number || (
                            <span className='text-muted-foreground text-sm'>Not set</span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Job Title */}
                    <div className='space-y-2'>
                      <Label
                        htmlFor='job_title'
                        className='text-sm font-medium flex items-center gap-2'
                      >
                        <User className='h-4 w-4 text-muted-foreground' />
                        Job Title
                      </Label>
                      {isEditing ? (
                        <Input
                          id='job_title'
                          value={formData.job_title}
                          onChange={e => handleInputChange('job_title', e.target.value)}
                          placeholder='Service Technician'
                        />
                      ) : (
                        <div className='px-3 py-2 border rounded-md bg-background min-h-[40px] flex items-center'>
                          {displayProfile.job_title || (
                            <span className='text-muted-foreground text-sm'>Not set</span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Department */}
                    <div className='space-y-2 sm:col-span-2'>
                      <Label
                        htmlFor='department'
                        className='text-sm font-medium flex items-center gap-2'
                      >
                        <Settings className='h-4 w-4 text-muted-foreground' />
                        Department
                      </Label>
                      {isEditing ? (
                        <Input
                          id='department'
                          value={formData.department}
                          onChange={e => handleInputChange('department', e.target.value)}
                          placeholder='Service Department'
                        />
                      ) : (
                        <div className='px-3 py-2 border rounded-md bg-background min-h-[40px] flex items-center'>
                          {displayProfile.department || (
                            <span className='text-muted-foreground text-sm'>Not set</span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Role - Read Only (scoped to current tenant) */}
                    {(() => {
                      const rolesForCurrentTenant =
                        userProfile?.roles?.filter(r => !r.tenantId || r.tenantId === tenantId) ??
                        [];
                      return rolesForCurrentTenant.length > 0 ? (
                        <div className='space-y-2 sm:col-span-2'>
                          <Label className='text-sm font-medium flex items-center gap-2'>
                            <Shield className='h-4 w-4 text-muted-foreground' />
                            Role
                          </Label>
                          <div className='px-3 py-2 border rounded-md bg-muted/50 min-h-[40px] flex items-center gap-2 flex-wrap'>
                            {rolesForCurrentTenant.map((role, idx) => (
                              <span
                                key={idx}
                                className='inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-primary/10 text-primary'
                              >
                                {role.displayName || role.name}
                              </span>
                            ))}
                          </div>
                          <p className='text-xs text-muted-foreground'>
                            Roles are managed by your administrator
                          </p>
                        </div>
                      ) : null;
                    })()}
                  </div>

                  {/* Edit Mode Actions */}
                  {isEditing && (
                    <div className='sticky bottom-0 bg-background -mx-6 px-6 pt-4 pb-4 border-t flex gap-2'>
                      <Button onClick={handleSaveProfile} disabled={saving} className='flex-1'>
                        {saving ? (
                          <>
                            <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2' />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className='h-4 w-4 mr-2' />
                            Save Changes
                          </>
                        )}
                      </Button>
                      <Button
                        variant='outline'
                        onClick={handleCancelEdit}
                        disabled={saving}
                        className='flex-1'
                      >
                        <X className='h-4 w-4 mr-2' />
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value='appearance' className='mt-0 flex-1 overflow-y-auto p-6 pt-4'>
              {/* Theme Settings */}
              <div className='space-y-4'>
                <h4 className='font-semibold text-sm uppercase tracking-wide text-muted-foreground'>
                  Appearance
                </h4>
                <div className='space-y-2'>
                  <Label className='text-sm font-medium'>Theme</Label>
                  <div className='grid grid-cols-3 gap-2'>
                    <Button
                      variant={theme === 'light' ? 'default' : 'outline'}
                      className='w-full'
                      onClick={() => setTheme('light')}
                    >
                      Light
                    </Button>
                    <Button
                      variant={theme === 'dark' ? 'default' : 'outline'}
                      className='w-full'
                      onClick={() => setTheme('dark')}
                    >
                      Dark
                    </Button>
                    <Button
                      variant={theme === 'system' ? 'default' : 'outline'}
                      className='w-full'
                      onClick={() => setTheme('system')}
                    >
                      System
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <DialogFooter className='p-6 pt-4 border-t shrink-0 bg-background flex flex-col-reverse sm:flex-row sm:justify-between gap-2'>
          <Button
            variant='destructive'
            onClick={handleSignOut}
            className='w-full sm:w-auto order-2 sm:order-1'
          >
            <LogOut className='h-4 w-4 mr-2' />
            Sign Out
          </Button>
          <Button
            variant='outline'
            onClick={() => onOpenChange(false)}
            className='w-full sm:w-auto order-1 sm:order-2'
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
/* eslint-enable max-lines-per-function, complexity, max-len, react/no-array-index-key */
